import React, { useState, useEffect, useRef } from 'react';
import useAuthStore from '../store/useAuthStore';
import useChatStore from '../store/useChatStore';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import useConfigStore from '../store/useConfigStore';
import PromptInput from '../components/chat/PromptInput';
import OmniIcon from '../components/icons/OmniIcon';

const SparklesIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" /></svg>;

const HomePage = () => {
    const { profile, user } = useAuthStore();
    const { createChat } = useChatStore();
    const { aiConfigs } = useConfigStore();
    const navigate = useNavigate();

    // Track local time greeting
    const [greeting, setGreeting] = useState('');
    const [prompt, setPrompt] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedModelConfigId, setSelectedModelConfigId] = useState(null);
    const [selectedExpertId, setSelectedExpertId] = useState(null);
    const textareaRef = useRef(null);

    useEffect(() => {
        const hour = new Date().getHours();
        if (hour < 12) setGreeting('Good morning');
        else if (hour < 18) setGreeting('Good afternoon');
        else setGreeting('Good evening');
    }, []);

    // Initial default model
    useEffect(() => {
        if (aiConfigs?.defaultConfigId && !selectedModelConfigId) {
            setSelectedModelConfigId(aiConfigs.defaultConfigId);
        }
    }, [aiConfigs, selectedModelConfigId]);


    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!prompt.trim() || isSubmitting) return;

        setIsSubmitting(true);
        try {
            // First 4 words as title
            const title = prompt.trim().split(' ').slice(0, 4).join(' ') + (prompt.split(' ').length > 4 ? '...' : '');

            const chatSettings = {
                targetDeviceId: null,
                expertId: selectedExpertId
            };

            if (typeof selectedModelConfigId === 'string') {
                chatSettings.modelConfigId = selectedModelConfigId;
                chatSettings.localBrainEnabled = false;
                chatSettings.localModel = null;
            } else if (selectedModelConfigId?.provider === 'ollama') {
                chatSettings.modelConfigId = null;
                chatSettings.localBrainEnabled = true;
                chatSettings.localModel = selectedModelConfigId.model;
            }

            // Create generic standalone chat with selected model
            const newSessionId = await createChat(title, chatSettings);

            // Navigate and pass the state so ChatRoomPage will auto-send
            navigate(`/chat/${newSessionId}`, { state: { initialMessage: prompt.trim() } });
        } catch (err) {
            console.error("Failed to start conversation:", err);
            setIsSubmitting(false);
        }
    };


    return (
        <div className="flex flex-col min-h-[100dvh] items-center justify-center px-4 py-8 max-w-3xl mx-auto w-full font-sans transition-colors duration-300">

            <div className="text-center space-y-4 mb-12 animate-in slide-in-from-bottom-4 duration-500 fade-in w-full relative">
                {/* Premium Glassmorphic AI Icon Container */}
                <div className="relative w-24 h-24 mx-auto mb-8 flex items-center justify-center">
                    {/* Rotating Gradient Glow Background */}
                    <motion.div
                        animate={{
                            rotate: 360,
                            scale: [1, 1.1, 1],
                        }}
                        transition={{
                            rotate: { duration: 8, repeat: Infinity, ease: "linear" },
                            scale: { duration: 4, repeat: Infinity, ease: "easeInOut" }
                        }}
                        className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-cyan-400/30 via-blue-500/20 to-purple-600/30 blur-2xl opacity-65"
                    />

                    {/* Main Icon Box (Static Position) */}
                    <motion.div
                        className="relative z-10 w-16 h-16 bg-[var(--bg-secondary)]/40 backdrop-blur-2xl text-cyan-400/90 rounded-3xl flex items-center justify-center border border-[var(--border-accent)] shadow-2xl overflow-hidden group"
                    >
                        {/* Dynamic Highlight Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />

                        {/* Omni Icon with Breathing Effect */}
                        <motion.div
                            animate={{
                                scale: [1, 1.05, 1],
                                opacity: [0.9, 1, 0.9],
                                filter: ["drop-shadow(0 0 0px #22d3ee)", "drop-shadow(0 0 12px rgba(34, 211, 238, 0.4))", "drop-shadow(0 0 0px #22d3ee)"]
                            }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        >
                            <OmniIcon className="w-10 h-10" />
                        </motion.div>
                    </motion.div>
                </div>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-light text-[var(--text-main)] tracking-tighter transition-all">
                    {greeting}, {profile?.displayName?.split(' ')[0] || 'User'}
                </h1>
                <p className="text-sm md:text-[15px] text-[var(--text-muted)] max-w-lg mx-auto leading-relaxed font-medium">
                    How can I assist you with your projects today?
                </p>
            </div>

            <PromptInput
                value={prompt}
                onChange={setPrompt}
                onSubmit={handleSubmit}
                placeholder="Message Omni..."
                isSubmitting={isSubmitting}
                selectedModelConfigId={selectedModelConfigId}
                onModelSelect={setSelectedModelConfigId}
                selectedExpertId={selectedExpertId}
                onExpertSelect={setSelectedExpertId}
                containerClassName="animate-in slide-in-from-bottom-6 duration-700 fade-in delay-150 fill-mode-both z-10"
            />

            <div className="w-full relative mt-4 z-10">
                <div className="text-center mt-4 text-xs text-[var(--text-muted)]">
                    Omni can make mistakes. Verify important information.
                </div>
                
                {/* Dynamic Download Link: Only visible on Web (non-Tauri) environments */}
                {!window.__TAURI_IPC__ && (
                    <div className="text-center mt-8">
                        <a 
                            href="/downloads/Omni_latest_aarch64.dmg" 
                            download 
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--bg-secondary)] border border-[var(--border-accent)] text-[var(--text-main)] text-sm font-medium hover:bg-[var(--bg-secondary)]/80 transition-all hover:scale-105 active:scale-95"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download for Mac
                        </a>
                    </div>
                )}
            </div>

        </div>
    );
};

export default HomePage;
